import cn from "classnames";
import { useMemo, type FC } from "react";
import { connect } from "react-redux";

import SelectableCard from "components/cards/selectableCard/SelectableCard";
import CardThumbnail from "components/cards/cardThumbnail/CardThumbnail";
import CardContent from "components/cards/cardContent/CardContent";

import { PackPublisherInfo } from "components/packs/packPublisherInfo/PackPublisherInfo";
import { PackStatusLabelWithIcon } from "components/packs/packStatusLabelWithIcon/PackStatusLabelWithIcon";
import { type RootState } from "features/Application/globaltypes/redux";
import { type AssignmentPeopleContext } from "enums";
import { type CardBase, type CardItemBase } from "interfaces";
import { PackPrice } from "components/packs/packPrice/PackPrice";
import { type AccountTypeEnum } from "../../../../../../interfaces/Account";

import "features/Accounts/PackAssignmentModal/packAssignmentSteps/packGridStep/addToAccountPackCard/addToAccountPackCard.scss";
import { LabelColors } from "features/Licensing/Packs/PackCard/types";
import { PricingTypes } from "enums/licensing/pricingTypes";

export type Item = CardItemBase & {
  description: string;
  logoUri: string;
  isRequested: boolean;
  isFullAccess: boolean;
  hasLicense: boolean;
  thumbnailImageUrl: string;
  isAddedToTemplateGroup?: boolean;
  isAdded?: boolean;
  pricePerUserPerMonth?: number;
  packPricingTypeId: number;
};

export interface AddToAccountPackCardProps extends CardBase<Item> {
  peopleType?: AssignmentPeopleContext;
  accountTypeId: AccountTypeEnum;
  parentAccountTypeId: AccountTypeEnum | null;
}

export const AddToAccountPackCard: FC<AddToAccountPackCardProps> = (props) => {
  const { onSelect, className, selected, peopleType, accountTypeId, parentAccountTypeId } = props;
  const {
    id,
    title,
    description,
    publisher,
    thumbnailImageUrl,
    isFullAccess,
    hasLicense,
    isRequested,
    isAddedToTemplateGroup,
    isAdded,
    pricePerUserPerMonth,
    packPricingTypeId,
  } = props.item;

  const isFree = packPricingTypeId === PricingTypes.Free;
  const isOtherPricingType = packPricingTypeId === PricingTypes.Other;
  const showPricing = !isOtherPricingType;

  const { disabled, tooltipContent } = useMemo(() => {
    if (isAddedToTemplateGroup) {
      return { disabled: true, tooltipContent: "The pack is already added to the Group Template" };
    }

    if (isFullAccess || hasLicense) {
      return { disabled: true, tooltipContent: `The ${peopleType} already has full access to this pack.` };
    }

    if (isAdded) {
      return { disabled: true, tooltipContent: "This pack has already been added to this account" };
    }

    return { disabled: false, tooltipContent: "" };
  }, [hasLicense, isAdded, isAddedToTemplateGroup, isFullAccess, peopleType]);

  return (
    <SelectableCard
      id={id}
      className={cn("add-to-account-pack-card", className)}
      onSelect={onSelect}
      disabled={disabled}
      selected={selected}
      tooltipContent={tooltipContent}
    >
      <>
        <CardThumbnail
          thumbnailUrl={thumbnailImageUrl}
          disabled={disabled}
          selected={selected}
          label={isFree ? "Free" : undefined}
          labelColor={LabelColors.FREE}
        />
        <CardContent title={title} preHeader={<PackPublisherInfo publisherName={publisher} />}>
          <div className="description-row">{description}</div>
          <div className="bottom-row">
            <PackStatusLabelWithIcon isRequested={isRequested} />
            {showPricing && (
              <PackPrice
                price={pricePerUserPerMonth}
                packPricingTypeId={packPricingTypeId}
                accountTypeId={accountTypeId}
                parentAccountTypeId={parentAccountTypeId}
              />
            )}
          </div>
        </CardContent>
      </>
    </SelectableCard>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  peopleType: state.packs.peopleIssueLicenseModal.modal.selectedPeopleType,
  accountTypeId: state.userProfile.accountTypeId,
  parentAccountTypeId: state.userProfile.parentAccountTypeId,
});

const ConnectedComponent = connect(mapStateToProps)(AddToAccountPackCard);

export default ConnectedComponent;
